/*
 * Engraving ("Gravur") pairing.
 *
 * An engraved product is two cart lines that share the `_Gravur ID` property:
 *   - the product itself (carries Gravurtext / Gravur Schriftart)
 *   - the "Gravur" fee product (carries the same properties plus `Related: 1`)
 *
 * Rules enforced here:
 *   1. A row is always addressed by its own line KEY (data-line) – never by its 1-based
 *      `line` index and never by looking up "input[data-index]" document-wide. On /cart the
 *      header drawer holds a second, stale copy of every row and a document-wide lookup hits
 *      that copy; that is what silently removed products while leaving the fee line behind.
 *   2. Quantity changes / removals of an engraved row are applied to the whole `_Gravur ID`
 *      group, resolved from the LIVE cart (/cart.js), not from whatever the DOM still shows.
 *   3. After every cart mutation the pairing is reconciled: orphan fee lines are removed,
 *      a missing fee line is re-attached, and fee quantity follows product quantity.
 */
const FLSK_ENGRAVING_ID_PROPERTY = '_Gravur ID';
const FLSK_ENGRAVING_FEE_PROPERTY = 'Related';

function flskFetchCartJson() {
  return fetch(`${routes.cart_url}.js`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then((response) =>
    response.json()
  );
}

function flskEngravingGroupLines(cart, connection) {
  if (!cart || !Array.isArray(cart.items) || !connection) return [];
  return cart.items.filter((item) => item.properties && item.properties[FLSK_ENGRAVING_ID_PROPERTY] === connection);
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (!cartItems) return;

      // Prefer the attributes rendered on this button / this row. Fall back to a lookup that is
      // scoped to the SAME cart container (never document-wide).
      const rowInput =
        this.closest('.cart-item')?.querySelector('input[data-index]') ||
        cartItems.querySelector(`input[data-index="${this.dataset.index}"]`);
      const connection = this.dataset.connection || rowInput?.dataset.connection;
      const lineKey = this.dataset.line || rowInput?.dataset.line;

      if (connection) {
        cartItems.updateEngravingGroupQuantity(connection, 0, null, null, this.dataset.index);
      } else if (lineKey) {
        cartItems.updateQuantity(this.dataset.index, 0, null, null, lineKey);
      } else {
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
    this.reconcileOnLoad();
  }

  // One reconciliation per page render, shared between the cart page and the drawer instance.
  reconcileOnLoad() {
    const now = Date.now();
    if (window.__flskEngravingReconcileAt && now - window.__flskEngravingReconcileAt < 2000) return;
    window.__flskEngravingReconcileAt = now;
    if (!this.querySelector('input[data-connection]')) return; // no engraving lines rendered
    flskFetchCartJson()
      .then((cart) => this.reconcileEngravingPairs(cart))
      .catch((e) => console.error(e));
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      const activeName = document.activeElement ? document.activeElement.getAttribute('name') : null;
      if (event.target.dataset.connection) {
        // Engraved product or its fee line: change the whole `_Gravur ID` group, resolved from the live cart.
        this.updateEngravingGroupQuantity(
          event.target.dataset.connection,
          inputValue,
          activeName,
          event.target.dataset.quantityVariantId,
          index
        );
      } else {
        this.updateQuantity(index, inputValue, activeName, event.target.dataset.quantityVariantId, event.target.dataset.line);
      }
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
    // The header cart drawer is rendered on /cart as well. Keep it in sync with every cart-page
    // change so it can never hold stale rows.
    if (document.getElementById('CartDrawer')) {
      sections.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
    }
    return sections;
  }

  // lineKey (item.key) is preferred; the 1-based `line` index is only a fallback for markup
  // that does not render data-line.
  updateQuantity(line, quantity, name, variantId, lineKey) {
    this.enableLoading(line);

    const payload = {
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };
    if (lineKey) {
      payload.id = lineKey;
    } else {
      payload.line = line;
    }
    const body = JSON.stringify(payload);

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        this.reconcileEngravingPairs(parsedState);
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  /**
   * Change the quantity of a whole engraving group (product + Gravur fee line) that share the
   * given `_Gravur ID`. The member lines are resolved from the live cart, so a stale DOM can
   * never split the pair.
   */
  updateEngravingGroupQuantity(connection, quantity, name, variantId, line) {
    this.enableLoading(line);
    return flskFetchCartJson()
      .then((cart) => {
        const keys = flskEngravingGroupLines(cart, connection).map((item) => item.key);
        if (!keys.length) {
          // Nothing left server-side for this group – the DOM was stale. Just redraw.
          return this.refreshSections();
        }
        return this.updateQuantityByKeys(keys, quantity, name, variantId, line, true);
      })
      .catch(() => {
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
        this.disableLoading(line);
      });
  }

  // Backwards compatible signature (two keys). Prefer updateQuantityByKeys / updateEngravingGroupQuantity.
  updateQuantityWithAddons(line_key, line_key2, quantity, name, variantId, line) {
    return this.updateQuantityByKeys([line_key, line_key2].filter(Boolean), quantity, name, variantId, line);
  }

  /**
   * Re-render all cart sections from the server (Section Rendering API) without changing the cart.
   */
  refreshSections() {
    const sections = this.getSectionsToRender();
    const ids = sections.map((section) => section.section).join(',');
    return fetch(`${routes.cart_url}?sections=${ids}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((response) => response.json())
      .then((rendered) => {
        sections.forEach((section) => {
          if (!rendered[section.section]) return;
          const container = document.getElementById(section.id);
          if (!container) return;
          const elementToReplace = container.querySelector(section.selector) || container;
          elementToReplace.innerHTML = this.getSectionInnerHTML(rendered[section.section], section.selector);
        });
      })
      .catch((e) => console.error(e))
      .finally(() => this.disableLoading());
  }

  /**
   * Make sure every `_Gravur ID` group in the cart is consistent:
   *  - fee line(s) without a product  -> remove the fee line(s)
   *  - product(s) without a fee line  -> re-add the fee line with the same engraving properties
   *  - fee quantity != product quantity -> align the fee quantity
   * Returns a promise resolving to true when the cart was changed (sections are re-rendered then).
   */
  reconcileEngravingPairs(cart) {
    if (!cart || !Array.isArray(cart.items)) return Promise.resolve(false);
    if (window.__flskEngravingReconciling) return Promise.resolve(false);

    const groups = {};
    cart.items.forEach((item) => {
      const connection = item.properties && item.properties[FLSK_ENGRAVING_ID_PROPERTY];
      if (!connection) return;
      groups[connection] = groups[connection] || { fee: [], products: [] };
      const isFee = item.properties[FLSK_ENGRAVING_FEE_PROPERTY] === '1';
      groups[connection][isFee ? 'fee' : 'products'].push(item);
    });

    const updates = {};
    const adds = [];
    Object.keys(groups).forEach((connection) => {
      const group = groups[connection];
      const productQty = group.products.reduce((sum, item) => sum + item.quantity, 0);

      if (group.fee.length && !group.products.length) {
        // Orphan fee line(s): the product is gone, the customer must not pay for an engraving.
        group.fee.forEach((fee) => (updates[fee.key] = 0));
        return;
      }
      if (group.products.length && !group.fee.length) {
        // Product still carries an engraving but the fee line is missing: re-attach it.
        const product = group.products[0];
        const feeVariantId = this.getEngravingFeeVariantId(cart, product);
        if (!feeVariantId) return;
        const properties = { [FLSK_ENGRAVING_FEE_PROPERTY]: '1' };
        Object.keys(product.properties).forEach((key) => (properties[key] = product.properties[key]));
        adds.push({ id: feeVariantId, quantity: productQty, properties });
        return;
      }
      if (group.fee.length && group.products.length) {
        group.fee.forEach((fee, index) => {
          // Exactly one fee line per group; extra ones are dropped, the first follows product qty.
          const wanted = index === 0 ? productQty : 0;
          if (fee.quantity !== wanted) updates[fee.key] = wanted;
        });
      }
    });

    if (!Object.keys(updates).length && !adds.length) return Promise.resolve(false);

    window.__flskEngravingReconciling = true;
    let chain = Promise.resolve();
    if (Object.keys(updates).length) {
      chain = chain.then(() =>
        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body: JSON.stringify({ updates }) } })
      );
    }
    if (adds.length) {
      chain = chain.then(() =>
        fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body: JSON.stringify({ items: adds }) } })
      );
    }
    return chain
      .then(() => this.refreshSections())
      .then(() => true)
      .catch((e) => {
        console.error(e);
        return false;
      })
      .finally(() => {
        window.__flskEngravingReconciling = false;
      });
  }

  // The Gravur fee variant for a product: rendered per row from the product metafield, with any
  // existing fee line in the cart as fallback.
  getEngravingFeeVariantId(cart, product) {
    const rowInput = document.querySelector(`input[data-line="${product.key}"][data-gravur-variant-id]`);
    if (rowInput && rowInput.dataset.gravurVariantId) return parseInt(rowInput.dataset.gravurVariantId, 10);
    const anyFee = cart.items.find((item) => item.properties && item.properties[FLSK_ENGRAVING_FEE_PROPERTY] === '1');
    return anyFee ? anyFee.variant_id : null;
  }

  /**
   * Set `quantity` on every given line key (Cart API /cart/update.js, key based) and re-render.
   */
  updateQuantityByKeys(keys, quantity, name, variantId, line, alreadyLoading) {
    if (!alreadyLoading) this.enableLoading(line);

    const updates = {};
    keys.forEach((key) => (updates[key] = quantity));
    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (
          quantityElement &&
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        this.reconcileEngravingPairs(parsedState);
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
